// Manual member-by-member builder — add each post/rail/slat line and it's drawn into the blueprint.
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { MEMBER_KINDS, MEMBER_POSITIONS, ALL_PROFILES } from "@/lib/panelBuilder";
import { MATERIAL_OPTIONS } from "@/lib/blueprintCalc";
import MembersPreview from "@/components/MembersPreview";

const KIND_LABEL = {
  post: "Post", rail: "Rail", slat: "Slat", louvre: "Louvre", blade: "Blade",
  tube: "Infill tube", brace: "Brace", laser_cut: "Laser-cut sheet", custom: "Member",
};

// Kinds that support per-member gap / spacing so the preview + PDF space them at the exact pitch.
const KINDS_WITH_GAP = new Set(["slat", "louvre", "blade", "tube"]);
const KIND_WIDTH_LABEL = { slat: "Slat", louvre: "Louvre", blade: "Blade", tube: "Tube" };
const KIND_WIDTH_DEFAULT = { slat: 65, louvre: 100, blade: 50, tube: 25 };
const KIND_GAP_DEFAULT = { slat: 10, louvre: 20, blade: 20, tube: 40 };

export const MembersBuilder = ({ members = [], onChange, layout, colourHex }) => {
  const [draft, setDraft] = useState({
    kind: "post", position: "left", profile: "40x40 SHS",
    length_mm: 2400, qty: 1, material: "aluminium",
    member_width_mm: 0, gap_mm: 0, note: "",
  });
  const positions = MEMBER_POSITIONS[draft.kind] || [];
  const showGap = KINDS_WITH_GAP.has(draft.kind);
  const set = (k, v) => setDraft((d) => {
    const patch = { [k]: v };
    if (k === "kind") {
      patch.position = (MEMBER_POSITIONS[v] || [])[0]?.value || "";
      patch.member_width_mm = KIND_WIDTH_DEFAULT[v] || 0;
      patch.gap_mm = KIND_GAP_DEFAULT[v] || 0;
    }
    return { ...d, ...patch };
  });
  const add = () => {
    if (!(draft.length_mm > 0) || !(draft.qty > 0)) return;
    onChange([...members, { ...draft, note: draft.note?.trim() || "" }]);
    setDraft((d) => ({ ...d, note: "" }));
  };
  return (
    <div className="space-y-3" data-testid="members-builder">
      <MembersPreview members={members} layout={layout} colourHex={colourHex} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 items-end">
        <div>
          <Label className="stamp-label text-slate-600">Piece</Label>
          <Select value={draft.kind} onValueChange={(v) => set("kind", v)}>
            <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="member-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{MEMBER_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {positions.length > 0 && (
          <div>
            <Label className="stamp-label text-slate-600">Position / direction</Label>
            <Select value={draft.position} onValueChange={(v) => set("position", v)}>
              <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="member-position"><SelectValue /></SelectTrigger>
              <SelectContent>{positions.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="stamp-label text-slate-600">Size / profile</Label>
          <Select value={draft.profile} onValueChange={(v) => set("profile", v)}>
            <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="member-profile"><SelectValue /></SelectTrigger>
            <SelectContent>{ALL_PROFILES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="stamp-label text-slate-600">Length (mm)</Label>
          <Input type="number" value={draft.length_mm} onChange={(e) => set("length_mm", Number(e.target.value) || 0)} className="rounded-sm h-9 mt-1 font-mono" data-testid="member-length" />
        </div>
        <div>
          <Label className="stamp-label text-slate-600">Qty</Label>
          <Input type="number" min="1" value={draft.qty} onChange={(e) => set("qty", parseInt(e.target.value, 10) || 1)} className="rounded-sm h-9 mt-1 font-mono" data-testid="member-qty" />
        </div>
        {showGap && (
          <>
            <div>
              <Label className="stamp-label text-slate-600">{KIND_WIDTH_LABEL[draft.kind]} width (mm)</Label>
              <Input type="number" min="1" value={draft.member_width_mm || 0}
                onChange={(e) => set("member_width_mm", Number(e.target.value) || 0)}
                className="rounded-sm h-9 mt-1 font-mono" data-testid="member-width-mm" />
            </div>
            <div>
              <Label className="stamp-label text-slate-600">Gap / spacing (mm)</Label>
              <Input type="number" min="0" value={draft.gap_mm || 0}
                onChange={(e) => set("gap_mm", Number(e.target.value) || 0)}
                className="rounded-sm h-9 mt-1 font-mono" data-testid="member-gap-mm" />
            </div>
          </>
        )}
        <div>
          <Label className="stamp-label text-slate-600">Material</Label>
          <Select value={draft.material} onValueChange={(v) => set("material", v)}>
            <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="member-material"><SelectValue /></SelectTrigger>
            <SelectContent>{MATERIAL_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="stamp-label text-slate-600">Note (optional)</Label>
          <Input value={draft.note} onChange={(e) => set("note", e.target.value)} placeholder="e.g. weld flush" className="rounded-sm h-9 mt-1" data-testid="member-note" />
        </div>
        <Button onClick={add} disabled={!(draft.length_mm > 0)} className="rounded-sm h-9 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="member-add-btn">
          <Plus size={14} className="mr-1" /> Add member
        </Button>
      </div>
      {members.length > 0 && (
        <div className="border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="members-list">
          {members.map((m, i) => (
            <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 text-xs" data-testid={`member-row-${i}`}>
              <span className="font-semibold text-slate-800">{KIND_LABEL[m.kind] || "Member"}{m.position ? ` (${m.position})` : ""}</span>
              <span className="font-mono text-slate-600">{m.profile} · {m.length_mm} mm × {m.qty}</span>
              {m.gap_mm > 0 && (
                <span className="font-mono text-cyan-700">{m.member_width_mm || 0} mm + {m.gap_mm} mm gap</span>
              )}
              <span className="text-slate-400">{m.material}</span>
              {m.note && <span className="text-slate-400 italic truncate">— {m.note}</span>}
              <Button size="sm" variant="ghost" onClick={() => onChange(members.filter((_, j) => j !== i))}
                className="rounded-sm h-6 w-6 p-0 ml-auto text-slate-400 hover:text-rose-600" data-testid={`member-delete-${i}`}>
                <Trash2 size={12} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MembersBuilder;
